import { action, computed, makeObservable, observable, runInAction } from "mobx";

import { Bundle, Dictionary, ImageURLs, sortBy } from "@noli/core";
import { ABCStore } from "@noli/business";

import { Events } from "@/utils/events";
import { MetaType } from "@/types/meta";

export type TaskType = {
  key: string;
  type: string;
  status: "pending" | "working" | "finished" | "exception";
  pending: number;
  isKafka: boolean;
  taskPack: {
    uri: string;
    data: Dictionary<any>;
  };
  data?: ImageURLs & {
    create_time?: number;
    start_time?: number;
    end_time?: number;
    duration?: number;
    meta?: MetaType["data"];
  };
  taskId?: string;
  serialNumber?: string;
  batchId?: string;
};
export type TaskDataType = TaskType["data"];
export interface ITasksStore {
  /**
   * 实际提交到后端的任务
   * - 如果后端用了 kafka，那么这里的 tasks 会记录返回的 uid
   * - 否则，这里的 tasks  将会是对应的 busyNodes 的 hash
   */
  tasks: Bundle<TaskType>;
  /**
   * 批次列表
   */
  batchs: Array<string>;
  /**
   * 前端用于防止用户操作过于频繁的字段
   */
  busyNodes: Set<string>;
}
export enum TrivialTaskEvent {
  // 执行
  EXEC = "exec",
}

function reOrderTrivialTasks(tasks: Bundle<TaskType>): void {
  const trivialTasks = tasks.filter((task) => !task.isKafka);
  const sorted = sortBy(
    trivialTasks,
    trivialTasks.map((task) => task.pending),
  );
  sorted.forEach((task, i) => (task.pending = i));
  sorted.forEach((task) => {
    if (task.pending === 0) {
      Events.getInstance().emit(TrivialTaskEvent.EXEC, { type: TrivialTaskEvent.EXEC, data: task });
    }
  });
}
class TasksStore extends ABCStore<ITasksStore> implements ITasksStore {
  tasks: Bundle<TaskType> = new Bundle([]);
  busyNodes: Set<string> = new Set();
  batchs: string[] = [];

  constructor() {
    super();
    makeObservable(this, {
      tasks: observable,
      busyNodes: observable,
      addTask: action,
      removeTask: action,
      updateTask: action,
      addBusyNode: action,
      removeBusyNode: action,
      pendingMaximum: computed,
      addBatch: action,
      removeBatch: action,
      addBatchTasks: action,
      removeBatchTasks: action,
      cancelBatchTasks: action,
    });
  }

  get info(): ITasksStore {
    return this;
  }

  addBatch(batchId: string) {
    runInAction(() => {
      this.batchs.push(batchId);
    });
  }

  removeBatch(batchId: string) {
    if (!this.batchs.includes(batchId)) return;
    const newBatchs = this.batchs.concat();
    newBatchs.splice(this.batchs.indexOf(batchId), 1);
    this.batchs = newBatchs;
  }

  addBatchTasks(tasks: Bundle<TaskType>): void {
    const _tasks = this.tasks.clone();
    for (const task of tasks.elements) {
      _tasks.push(task);
    }
    reOrderTrivialTasks(_tasks);
    this.tasks = _tasks;
  }

  removeBatchTasks(tasks: Bundle<TaskType> | Array<TaskType>): void {
    console.log("removeBatchTasks: ", tasks);
    const _tasks = this.tasks.clone();
    const tasksArr = Array.isArray(tasks) ? tasks : tasks.elements;
    for (const task of tasksArr) {
      if (_tasks.get(task.key)) {
        _tasks.remove(task.key);
      }
    }
    reOrderTrivialTasks(_tasks);
    this.tasks = _tasks;
  }

  cancelBatchTasks(batchId: string) {
    runInAction(() => {
      tasksStore.removeBatch(batchId);
      const tasks = tasksStore.tasks.filter((task) => task.batchId === batchId);
      tasksStore.removeBatchTasks(tasks);
    });
  }

  addTask(task: TaskType): void {
    const tasks = this.tasks.clone();
    tasks.push(task);
    reOrderTrivialTasks(tasks);
    this.tasks = tasks;
  }
  removeTask(taskKey: string | TaskType): void;
  removeTask(task: TaskType): void {
    const tasks = this.tasks.clone();
    const taskKey = typeof task === "string" ? task : task.key;
    if (this.tasks.get(taskKey)) {
      tasks.remove(taskKey);
      reOrderTrivialTasks(tasks);
      this.tasks = tasks;
    }
  }
  updateTask(newTask: TaskType): void {
    const tasks = this.tasks.clone();
    tasks.set(newTask);
    reOrderTrivialTasks(tasks);
    this.tasks = tasks;
  }

  addBusyNode(...aliases: string[]): void {
    this.busyNodes = new Set(Array.from(this.busyNodes).concat(aliases));
  }
  removeBusyNode(...aliases: string[]): void {
    const busy = new Set(Array.from(this.busyNodes));
    aliases.forEach((alias) => busy.delete(alias));
    this.busyNodes = busy;
  }

  get pendingMaximum() {
    return Math.max(...this.tasks.map((task) => task.pending), 0);
  }
}

export const tasksStore = new TasksStore();
